import { GridColumn } from '@progress/kendo-react-grid'
import React from 'react'
import { useTranslation } from 'react-i18next'
import * as R from 'remeda'

import { createBreadcrumbWithI18n } from '~/apps/ninesegsAnalyzer/routes/paths'
import {
  PageContainer,
  type PageContainerProps,
} from '~/components/elements/PageContainer'
import {
  StyledGrid,
  type StyledGridProps,
} from '~/components/elements/StyledGrid'
import { VStack } from '~/components/elements/VStack'
import { useLanguageTextWidth } from '~/hooks/useLanguageTextWidth'
import { useScrollToBottom } from '~/hooks/useScrollToBottom'

import { useArchivedStrategyMemosInfiniteQuery } from '../hooks/useArchivedStrategyMemosInfiniteQuery'
import { useBrandAccountId } from '../hooks/useBrandAccountId'

import { CommandsCell } from './CommandsCell'
import { Dialogs } from './Dialogs'

export const StrategyMemoArchiveList = () => {
  const brandAccountId = useBrandAccountId()
  const { t } = useTranslation()

  const createBreadcrumb = React.useMemo(() => createBreadcrumbWithI18n(t), [t])
  const breadcrumbs = React.useMemo<PageContainerProps['breadcrumbs']>(
    () => [
      createBreadcrumb('top', {}),
      createBreadcrumb('strategyMemoManagement'),
      createBreadcrumb('strategyMemoList', { brandAccountId }),
      createBreadcrumb('strategyMemoArchiveList', { brandAccountId }),
    ],
    [brandAccountId, createBreadcrumb],
  )

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useArchivedStrategyMemosInfiniteQuery()
  const strategyMemos = React.useMemo(
    () => R.pipe(data?.pages ?? [], R.flat()),
    [data?.pages],
  )

  const handleGridScroll = useScrollToBottom<EH<StyledGridProps, 'onScroll'>>({
    getElement: (event) => event.nativeEvent.target,
    onScrollToBottom: () => {
      if (hasNextPage && !isFetchingNextPage) {
        fetchNextPage()
      }
    },
  })

  const restoreButtonWidth = useLanguageTextWidth({
    'ja-JP': 124,
    'en-US': 103,
  })
  const deleteButtonWidth = useLanguageTextWidth({ 'ja-JP': 152, 'en-US': 145 })
  const commandsCellWidth = 12 + restoreButtonWidth + 4 + deleteButtonWidth + 12

  return (
    <PageContainer breadcrumbs={breadcrumbs}>
      <VStack spacing={0} verticalAppearance="stretch">
        <StyledGrid data={strategyMemos} onScroll={handleGridScroll}>
          <GridColumn field="title" title={t('name')} />

          <GridColumn field="writer" title={t('owner')} />

          <GridColumn cell={CommandsCell} width={commandsCellWidth} />
        </StyledGrid>
      </VStack>

      <Dialogs />
    </PageContainer>
  )
}
